import React, { useContext, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import "./InventoryModule.css";

const ROLE_OPTIONS = [
  ["3", "Ops / Admin"],
  ["4", "Sales"],
  ["5", "Finance"],
  ["7", "Cafe"],
  ["9", "Personal Trainer"],
  ["10", "Nutrition"],
  ["11", "Fitness Manager"],
  ["12", "HR"],
  ["15", "Physio"],
  ["16", "Social Media"],
  ["17", "General Trainer"],
];

const TYPE_LABELS = {
  receive: "Receive",
  issue: "Issue",
  adjust_in: "Adjust in",
  adjust_out: "Adjust out",
  transfer_out: "Transfer out",
  transfer_in: "Transfer in",
  sale_usage: "Sale usage",
  shortage: "Shortage",
};

const branchName = (id) => (String(id) === "15" ? "F-11" : String(id) === "1" ? "G-13" : id);
const staffName = (row) => `${row?.first_name || ""} ${row?.last_name || ""}`.replace(/\s+/g, " ").trim();

function messageOf(err, fallback) {
  return err?.response?.data?.message || fallback;
}

function explain(message) {
  return Swal.fire({
    title: "Please check this",
    text: message,
    icon: "warning",
    confirmButtonColor: "#e8002b",
  });
}

function titleOf(key) {
  if (key === "categories") return "category name";
  if (key === "brands") return "brand name";
  if (key === "colors") return "color";
  if (key === "descriptions") return "description";
  return "name";
}

const emptyProduct = () => ({
  name: "",
  category_id: "",
  uom_id: "",
  brand_id: "",
  color_id: "",
  description: "",
  reorder_level: "",
  sku: "",
});

function downloadCsv(filename, headers, rows) {
  const lines = [headers.join(",")];
  rows.forEach((row) => {
    lines.push(row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function InventoryModule() {
  const { state } = useContext(NoteState);
  const { role, branch_id: loginBranchId } = state;
  const canPick = String(role) === "1";
  const [picked, setPicked] = useState(canPick ? "" : String(loginBranchId || ""));
  const location = useLocation();
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!canPick) setPicked(String(loginBranchId || ""));
  }, [canPick, loginBranchId]);

  useEffect(() => {
    UserServices.inventoryMeta()
      .then((res) => setMeta(res.data?.data || null))
      .catch((err) => Swal.fire("Inventory", messageOf(err, "Could not load inventory."), "error"));
  }, []);

  const access = meta?.access || {};
  const concrete = picked === "1" || picked === "15";
  const apiBranch = concrete ? picked : undefined;
  const path = location.pathname.replace(/\/$/, "") || "/inventory";
  const page = path === "/inventory"
    ? "dashboard"
    : path.startsWith("/inventory/stock")
      ? "stock"
      : path.startsWith("/inventory/receive")
        ? "receive"
          : path.startsWith("/inventory/issue")
            ? "issue"
            : path.startsWith("/inventory/demands")
              ? "demands"
              : path.startsWith("/inventory/water")
                ? "water"
                : path.startsWith("/inventory/transfer")
            ? "transfer"
            : path.startsWith("/inventory/products")
              ? "products"
              : path.startsWith("/inventory/catalog") || path.startsWith("/inventory/categories") || path.startsWith("/inventory/units") || path.startsWith("/inventory/brands") || path.startsWith("/inventory/setup")
                ? "catalog"
                : path.startsWith("/inventory/movements")
                  ? "movements"
                  : path.startsWith("/inventory/recipes")
                    ? "recipes"
                    : "reports";

  const tabs = [
    { to: "/inventory", label: "Dashboard", end: true },
    { to: "/inventory/stock", label: "Stock" },
    { to: "/inventory/receive", label: "Receive" },
    { to: "/inventory/issue", label: "Issue" },
    { to: "/inventory/demands", label: "Demands" },
    { to: "/inventory/water-bottles", label: "Water bottles" },
    access.can_transfer ? { to: "/inventory/transfer", label: "Move" } : null,
    { to: "/inventory/movements", label: "Movements" },
    { to: "/inventory/recipes", label: "Cafe rules" },
    { to: "/inventory/reports", label: "Reports" },
    access.can_setup ? { to: "/inventory/products", label: "Products" } : null,
    access.can_setup ? { to: "/inventory/catalog", label: "Catalog" } : null,
  ].filter(Boolean);

  const needsBranch = page !== "dashboard";
  const locked = needsBranch && !concrete;

  return (
    <div className="wrapper inv-shell">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton hideSearch />
          <div className="inv-top">
            <div>
              <p className="inv-kicker">Inventory</p>
              <h1>{page === "dashboard" ? "Dashboard" : tabs.find((tab) => tab.to !== "/inventory" && path.startsWith(tab.to))?.label || "Inventory"}</h1>
            </div>
            <div className="inv-branch">
              <label htmlFor="inventory-branch">Branch</label>
              <select
                id="inventory-branch"
                className="form-select"
                value={canPick ? picked : String(loginBranchId || "")}
                disabled={!canPick}
                onChange={(e) => setPicked(e.target.value)}
              >
                {canPick ? <option value="">Select a branch</option> : null}
                {canPick ? <option value="all">Both branches</option> : null}
                {(canPick || String(loginBranchId) === "15") ? <option value="15">F-11</option> : null}
                {(canPick || String(loginBranchId) === "1") ? <option value="1">G-13</option> : null}
              </select>
            </div>
          </div>
          <nav className="inv-nav">
            {tabs.map((tab) => (
              <NavLink key={tab.to} to={tab.to} end={tab.end}>
                {tab.label}
              </NavLink>
            ))}
          </nav>
          {!meta ? (
            <div className="inv-panel">Loading inventory...</div>
          ) : locked ? (
            <div className="inv-panel inv-gate">
              <h2>Choose a branch first</h2>
              <p>Pick F-11 or G-13 above. This page stays closed until a branch is selected. The dashboard still shows both branches together.</p>
            </div>
          ) : page === "dashboard" ? (
            <DashboardPage apiBranch={apiBranch} canPick={canPick} branchLabel={picked === "15" ? "F-11" : picked === "1" ? "G-13" : "Both branches"} />
          ) : page === "receive" || page === "issue" || page === "transfer" ? (
            <div className="inv-panel">
              <StockEntryPage mode={page} apiBranch={apiBranch} role={role} loginBranchId={concrete ? picked : loginBranchId} />
            </div>
          ) : page === "demands" ? (
            <div className="inv-panel"><DemandsPage apiBranch={apiBranch} canDecide={!!access.can_decide} /></div>
          ) : page === "water" ? (
            <div className="inv-panel"><WaterBottlesPage apiBranch={apiBranch} /></div>
          ) : page === "products" ? (
            <div className="inv-panel"><SetupPage meta={meta} section="products" /></div>
          ) : page === "catalog" ? (
            <CatalogPage meta={meta} />
          ) : page === "movements" ? (
            <div className="inv-panel"><MovementsPage meta={meta} apiBranch={apiBranch} /></div>
          ) : page === "recipes" ? (
            <div className="inv-panel"><RecipesPage meta={meta} apiBranch={apiBranch} /></div>
          ) : page === "reports" ? (
            <div className="inv-panel"><ReportsPage meta={meta} apiBranch={apiBranch} /></div>
          ) : (
            <div className="inv-panel"><StockPage meta={meta} apiBranch={apiBranch} role={role} loginBranchId={concrete ? picked : loginBranchId} /></div>
          )}
        </div>
      </main>
    </div>
  );
}

function KpiRow({ title, data, navigate }) {
  const cards = data?.cards || {};
  const tiles = [
    ["Items", cards.items ?? 0, "bi-box-seam", "", "/inventory/products"],
    ["Low stock", cards.low ?? 0, "bi-exclamation-triangle", "tone-red", "/inventory/reports?kind=low"],
    ["Shortages", cards.shortages ?? 0, "bi-exclamation-circle", "tone-red", "/inventory/reports?kind=shortages"],
    ["Cafe usage", cards.usage?.count ?? 0, "bi-cup-hot", "", "/inventory/reports?kind=usage"],
    ["Received", cards.received?.count ?? 0, "bi-box-arrow-in-down", "", "/inventory/receive"],
    ["Issued", cards.issued?.count ?? 0, "bi-box-arrow-up", "", "/inventory/issue"],
    ["Transfers", cards.transfers?.count ?? 0, "bi-arrow-left-right", "", "/inventory/transfer"],
  ];
  return (
    <section className="mb-4">
      <h2 className="inv-section-title">{title}</h2>
      <div className="inv-kpis">
        {tiles.map(([label, value, icon, tone, to]) => (
          <button key={label} type="button" className={`inv-kpi ${tone}`} onClick={() => navigate(to)}>
            <div>
              <span>{label}</span>
              <b>{value}</b>
            </div>
            <i className={`bi ${icon}`} />
          </button>
        ))}
      </div>
      <div className="row g-3">
        <div className="col-lg-4">
          <div className="inv-panel h-100">
            <h3 className="inv-section-title">Low stock</h3>
            <table className="table table-sm mb-0">
              <tbody>
                {(data?.low || []).map((row) => (
                  <tr key={`${row.item_id}-${row.branch_id}`}>
                    <td>{row.name}<div className="small text-muted">{row.branch_name}</div></td>
                    <td className="text-end">{row.qty_on_hand} {row.uom}</td>
                  </tr>
                ))}
                {!data?.low?.length ? <tr><td>Nothing is at the reorder level.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="inv-panel h-100">
            <h3 className="inv-section-title">Most used</h3>
            <table className="table table-sm mb-0">
              <tbody>
                {(data?.top_products || []).map((row, index) => (
                  <tr key={`p-${index}`}><td>{row.product_name || "Cafe product"}</td><td className="text-end">{row.sale_count} sales</td></tr>
                ))}
                {(data?.top_items || []).map((row, index) => (
                  <tr key={`i-${index}`}><td>{row.item_name}</td><td className="text-end">{row.qty} {row.uom}</td></tr>
                ))}
                {!data?.top_products?.length && !data?.top_items?.length ? <tr><td>No usage in these dates.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="inv-panel h-100">
            <h3 className="inv-section-title">Latest movements</h3>
            <table className="table table-sm mb-2">
              <tbody>
                {(data?.recent || []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.item_name}<div className="small text-muted">{TYPE_LABELS[row.type] || row.type} · {branchName(row.branch_id)}{row.issued_to_name ? ` · ${row.issued_to_name}` : ""}</div></td>
                    <td className="text-end">{row.qty} {row.uom}</td>
                  </tr>
                ))}
                {!data?.recent?.length ? <tr><td>No movements in these dates.</td></tr> : null}
              </tbody>
            </table>
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => navigate("/inventory/movements")}>Open the full ledger</button>
          </div>
        </div>
        <div className="col-12">
          <div className="inv-panel">
            <h3 className="inv-section-title">Stock of all products</h3>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Branch</th>
                    <th className="text-end">On hand</th>
                    <th className="text-end">Reorder</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.stock || []).map((row) => (
                    <tr key={`${row.item_id}-${row.branch_id}`} className={row.low ? "inventory-low" : ""}>
                      <td>{row.name}</td>
                      <td>{row.category_name}</td>
                      <td>{row.branch_name}</td>
                      <td className="text-end">{row.qty_on_hand} {row.uom}</td>
                      <td className="text-end">{row.reorder_level}</td>
                    </tr>
                  ))}
                  {!data?.stock?.length ? <tr><td colSpan="5">No products yet.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DashboardPage({ apiBranch, canPick, branchLabel }) {
  const navigate = useNavigate();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [branchData, setBranchData] = useState(null);
  const [allData, setAllData] = useState(null);

  const load = () => {
    const dates = { start_date: start || undefined, end_date: end || undefined };
    const calls = [];
    if (canPick) {
      calls.push(UserServices.inventoryDashboard(dates).then((res) => setAllData(res.data?.data || null)));
    }
    if (apiBranch) {
      calls.push(UserServices.inventoryDashboard({ ...dates, branch_id: apiBranch }).then((res) => setBranchData(res.data?.data || null)));
    } else {
      setBranchData(null);
    }
    Promise.all(calls).catch((err) => Swal.fire("Dashboard", messageOf(err, "Could not load the dashboard."), "error"));
  };

  useEffect(() => { load(); }, [apiBranch, canPick]);

  return (
    <>
      <div className="inv-panel mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label">From</label>
            <input type="date" className="form-control" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label">To</label>
            <input type="date" className="form-control" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div className="col-md-2">
            <button type="button" className="btn btn-danger w-100" onClick={load}>Show</button>
          </div>
          <div className="col-md-4 text-muted small">
            Stock levels are the quantity on hand now. The other counts use the dates above.
          </div>
        </div>
      </div>
      {apiBranch ? <KpiRow title={branchLabel} data={branchData} navigate={navigate} /> : null}
      {canPick ? <KpiRow title="Both branches" data={allData} navigate={navigate} /> : null}
    </>
  );
}

function StockEntryPage({ mode, apiBranch, role, loginBranchId }) {
  const canPick = String(role) === "1";
  const [items, setItems] = useState([]);
  const [stock, setStock] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({
    item_id: "",
    qty: "",
    note: "",
    issued_to: "",
    branch_id: apiBranch || (loginBranchId ? String(loginBranchId) : "15"),
    direction: "out",
  });
  const staffBranch = canPick ? form.branch_id : (apiBranch || loginBranchId);

  useEffect(() => {
    UserServices.inventoryItems({ active_only: 1 }).then((res) => setItems(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode !== "issue" || !staffBranch) {
      setStaff([]);
      return;
    }
    UserServices.getStaffName(staffBranch)
      .then((res) => {
        const list = (res.data?.data || []).slice().sort((a, b) => staffName(a).localeCompare(staffName(b)));
        setStaff(list);
      })
      .catch(() => setStaff([]));
  }, [mode, staffBranch]);

  useEffect(() => {
    const branch = canPick ? form.branch_id : apiBranch;
    UserServices.inventoryStock({ branch_id: branch || undefined })
      .then((res) => setStock(res.data?.data || []))
      .catch(() => {});
  }, [form.branch_id, apiBranch, canPick]);

  const onHand = stock.find((row) => String(row.item_id) === String(form.item_id) && String(row.branch_id) === String(canPick ? form.branch_id : apiBranch || row.branch_id));
  const other = String(form.branch_id) === "1" ? "15" : "1";

  const submit = async () => {
    if (!form.item_id) return explain("Choose the item first.");
    const qty = Number(form.qty);
    if (!(qty > 0)) return explain("Type the quantity. It has to be more than zero.");
    if (mode === "issue" && !form.issued_to) return explain("Choose the staff member this is given to.");
    if (mode === "issue" && !form.note.trim()) return explain("Write a short reason, for example given to the floor.");
    if ((mode === "issue" || mode === "transfer") && onHand && qty > Number(onHand.qty_on_hand)) {
      return explain(`Only ${onHand.qty_on_hand} ${onHand.uom} is on hand. Take less than that.`);
    }
    try {
      if (mode === "transfer") {
        await UserServices.inventoryMove("transfer", {
          item_id: form.item_id,
          qty: form.qty,
          note: form.note,
          from_branch_id: form.branch_id,
          to_branch_id: other,
        });
      } else if (mode === "issue") {
        await UserServices.inventoryMove("issue", {
          item_id: form.item_id,
          qty: form.qty,
          note: form.note,
          branch_id: form.branch_id,
          issued_to: form.issued_to,
        });
      } else {
        await UserServices.inventoryMove("receive", {
          item_id: form.item_id,
          qty: form.qty,
          note: form.note,
          branch_id: form.branch_id,
        });
      }
      const person = staff.find((row) => String(row.id) === String(form.issued_to));
      Swal.fire({ title: "Saved", text: mode === "receive" ? "Stock is added." : mode === "issue" ? `Stock is issued to ${staffName(person)}.` : "Stock is moved.", icon: "success", timer: 1400, showConfirmButton: false });
      setForm({ ...form, item_id: "", qty: "", note: "", issued_to: "" });
      const branch = canPick ? form.branch_id : apiBranch;
      const res = await UserServices.inventoryStock({ branch_id: branch || undefined });
      setStock(res.data?.data || []);
    } catch (err) {
      Swal.fire("Stock", messageOf(err, "Could not update stock."), "error");
    }
  };

  const title = mode === "receive" ? "Receive stock" : mode === "issue" ? "Issue stock" : "Move stock";
  if (mode === "transfer" && String(role) !== "1" && String(role) !== "3") {
    return <p className="mb-0">Only Super Admin and branch office logins can move stock.</p>;
  }

  return (
    <>
      <h6 className="mb-3">{title}</h6>
      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label">Item</label>
          <select className="form-select" value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })}>
            <option value="">Choose an item</option>
            {items.map((row) => <option key={row.id} value={row.id}>{row.name} ({row.uom?.abbreviation})</option>)}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">{mode === "transfer" ? "From branch" : "Branch"}</label>
          {canPick ? (
            <select className="form-select" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value, issued_to: "" })}>
              <option value="15">F-11</option>
              <option value="1">G-13</option>
            </select>
          ) : (
            <input className="form-control" value={branchName(loginBranchId)} disabled />
          )}
        </div>
        {mode === "transfer" ? (
          <div className="col-md-2">
            <label className="form-label">To</label>
            <input className="form-control" value={branchName(other)} disabled />
          </div>
        ) : null}
        {mode === "issue" ? (
          <div className="col-md-4">
            <label className="form-label">Given to</label>
            <select className="form-select" value={form.issued_to} onChange={(e) => setForm({ ...form, issued_to: e.target.value })}>
              <option value="">Choose the staff member</option>
              {staff.map((row) => <option key={row.id} value={row.id}>{staffName(row)}</option>)}
            </select>
            {!staff.length ? <div className="form-text">No active staff at this branch.</div> : null}
          </div>
        ) : null}
        <div className="col-md-2">
          <label className="form-label">Quantity</label>
          <input className="form-control" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
        </div>
        <div className="col-md-4">
          <label className="form-label">{mode === "issue" ? "Reason" : "Note"}</label>
          <input className="form-control" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-dark w-100" onClick={submit}>Save</button>
        </div>
      </div>
      {onHand ? <p className="mt-3 mb-0">On hand at this branch: <strong>{onHand.qty_on_hand} {onHand.uom}</strong></p> : null}
    </>
  );
}

function StockPage({ meta, apiBranch, role, loginBranchId }) {
  const navigate = useNavigate();
  const [categoryId, setCategoryId] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState(null);

  const load = () => {
    UserServices.inventoryStock({ branch_id: apiBranch, category_id: categoryId || undefined, q: q || undefined })
      .then((res) => setRows(res.data?.data || []))
      .catch((err) => Swal.fire("Stock", messageOf(err, "Could not load stock."), "error"));
  };

  useEffect(() => { load(); }, [apiBranch, categoryId]);

  return (
    <>
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <select className="form-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">All categories</option>
            {(meta.categories || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </div>
        <div className="col-md-5">
          <input className="form-control" placeholder="Search item" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="col-md-3">
          <button className="btn btn-dark w-100" onClick={load}>Search</button>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Item</th><th>Category</th><th>Branch</th><th>On hand</th><th>Reorder</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.item_id}-${row.branch_id}`} className={row.low || row.shortage_count > 0 ? "inventory-low" : ""}>
                <td>
                  {row.name}
                  {row.shortage_count > 0 ? <div className="inventory-short">Shortage recorded</div> : null}
                </td>
                <td>{row.category_name}</td>
                <td>{row.branch_name}</td>
                <td>{row.qty_on_hand} {row.uom}</td>
                <td>{row.reorder_level}</td>
                <td className="text-nowrap">
                  <button className="btn btn-sm btn-outline-dark me-1" onClick={() => setAction({ type: "receive", row })}>Receive</button>
                  <button className="btn btn-sm btn-outline-dark me-1" onClick={() => setAction({ type: "issue", row })}>Issue</button>
                  <button className="btn btn-sm btn-outline-dark me-1" onClick={() => setAction({ type: "adjust", row })}>Adjust</button>
                  {meta.access?.can_transfer ? (
                    <button className="btn btn-sm btn-outline-dark me-1" onClick={() => setAction({ type: "transfer", row })}>Transfer</button>
                  ) : null}
                  <button className="btn btn-sm btn-link" onClick={() => navigate(`/inventory/movements?item_id=${row.item_id}&branch_id=${row.branch_id}`)}>History</button>
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan="6">No items in the categories you can see.</td></tr> : null}
          </tbody>
        </table>
      </div>
      {action ? (
        <StockAction
          action={action}
          role={role}
          loginBranchId={loginBranchId}
          onClose={() => setAction(null)}
          onDone={() => { setAction(null); load(); }}
        />
      ) : null}
    </>
  );
}

function StockAction({ action, role, loginBranchId, onClose, onDone }) {
  const { row, type } = action;
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [direction, setDirection] = useState("out");
  const [issuedTo, setIssuedTo] = useState("");
  const [staff, setStaff] = useState([]);
  const [branchId, setBranchId] = useState(String(row.branch_id));
  const other = String(row.branch_id) === "15" ? "1" : "15";
  const needsNote = type === "issue" || type === "adjust";

  useEffect(() => {
    if (type !== "issue") return;
    UserServices.getStaffName(branchId)
      .then((res) => {
        const list = (res.data?.data || []).slice().sort((a, b) => staffName(a).localeCompare(staffName(b)));
        setStaff(list);
      })
      .catch(() => setStaff([]));
  }, [type, branchId]);

  const submit = async () => {
    const amount = Number(qty);
    if (!(amount > 0)) return explain("Type the quantity. It has to be more than zero.");
    if (type === "issue" && !issuedTo) return explain("Choose the staff member this is given to.");
    if (needsNote && !note.trim()) return explain(type === "adjust" ? "Write why you are changing this stock." : "Write a short reason, for example given to the floor.");
    const leaving = type === "issue" || type === "transfer" || (type === "adjust" && direction === "out");
    if (leaving && String(branchId) === String(row.branch_id) && amount > Number(row.qty_on_hand)) {
      return explain(`Only ${row.qty_on_hand} ${row.uom} is on hand. Take less than that.`);
    }
    const payload = {
      item_id: row.item_id,
      qty,
      note,
      branch_id: branchId,
    };
    try {
      if (type === "transfer") {
        await UserServices.inventoryMove("transfer", {
          item_id: row.item_id,
          qty,
          note,
          from_branch_id: row.branch_id,
          to_branch_id: other,
        });
      } else if (type === "adjust") {
        await UserServices.inventoryMove("adjust", { ...payload, direction });
      } else if (type === "issue") {
        await UserServices.inventoryMove("issue", { ...payload, issued_to: issuedTo });
      } else {
        await UserServices.inventoryMove(type, payload);
      }
      onDone();
    } catch (err) {
      Swal.fire("Stock", messageOf(err, "Could not update stock."), "error");
    }
  };

  return (
    <div className="border rounded p-3 mt-3">
      <h6 className="mb-3">{TYPE_LABELS[type] || type}: {row.name} ({row.uom})</h6>
      <div className="row g-2">
        {type !== "transfer" && String(role) === "1" ? (
          <div className="col-md-3">
            <label className="form-label">Branch</label>
            <select className="form-select" value={branchId} onChange={(e) => { setBranchId(e.target.value); setIssuedTo(""); }}>
              <option value="15">F-11</option>
              <option value="1">G-13</option>
            </select>
          </div>
        ) : (
          <div className="col-md-3">
            <label className="form-label">Branch</label>
            <input className="form-control" value={type === "transfer" ? `${branchName(row.branch_id)} to ${branchName(other)}` : branchName(String(role) === "1" ? branchId : loginBranchId)} disabled />
          </div>
        )}
        {type === "issue" ? (
          <div className="col-md-3">
            <label className="form-label">Given to</label>
            <select className="form-select" value={issuedTo} onChange={(e) => setIssuedTo(e.target.value)}>
              <option value="">Choose the staff member</option>
              {staff.map((person) => <option key={person.id} value={person.id}>{staffName(person)}</option>)}
            </select>
          </div>
        ) : null}
        <div className="col-md-2">
          <label className="form-label">Quantity</label>
          <input className="form-control" value={qty} onChange={(e) => setQty(e.target.value)} />
        </div>
        {type === "adjust" ? (
          <div className="col-md-2">
            <label className="form-label">Direction</label>
            <select className="form-select" value={direction} onChange={(e) => setDirection(e.target.value)}>
              <option value="in">In</option>
              <option value="out">Out</option>
            </select>
          </div>
        ) : null}
        <div className="col-md-3">
          <label className="form-label">{needsNote ? "Reason" : "Note"}</label>
          <input className="form-control" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="col-md-2 d-flex align-items-end gap-2">
          <button className="btn btn-dark" onClick={submit}>Save</button>
          <button className="btn btn-outline-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function CatalogPage({ meta }) {
  const [catalog, setCatalog] = useState(meta);
  const [drafts, setDrafts] = useState({ categories: "", uoms: "", brands: "", colors: "", descriptions: "" });
  const [unit, setUnit] = useState({ abbreviation: "", base_code: "piece", factor: "1" });

  const refresh = () => {
    UserServices.inventoryMeta().then((res) => setCatalog(res.data?.data || meta)).catch(() => {});
  };

  const save = async (path, payload, clear) => {
    try {
      await UserServices.inventorySave(path, payload);
      if (clear) clear();
      refresh();
      Swal.fire({ title: "Saved", text: "It is on the list now. You can pick it when you add a product.", icon: "success", timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Catalog", messageOf(err, "Could not save."), "error");
    }
  };

  const addSimple = (key, path) => {
    const name = String(drafts[key] || "").trim();
    if (!name) return explain(`Type the ${titleOf(key)} first.`);
    save(path, { name }, () => setDrafts((current) => ({ ...current, [key]: "" })));
  };

  const addUnit = () => {
    const name = String(drafts.uoms || "").trim();
    if (!name) return explain("Type the unit name first, for example Kilogram.");
    if (!unit.abbreviation.trim()) return explain("Type the short form, for example kg.");
    if (!(Number(unit.factor) > 0)) return explain("The factor must be more than zero. Use 1 if you are not sure.");
    save("uoms", { name, ...unit, abbreviation: unit.abbreviation.trim() }, () => {
      setDrafts((current) => ({ ...current, uoms: "" }));
      setUnit({ abbreviation: "", base_code: "piece", factor: "1" });
    });
  };

  if (!meta.access?.can_setup) {
    return <p className="mb-0">Only Super Admin can edit the catalog.</p>;
  }

  const blocks = [
    ["Categories", "categories", catalog.categories || [], "categories", null],
    ["Brands", "brands", catalog.brands || [], "brands", null],
    ["Colors", "colors", catalog.colors || [], "colors", null],
    ["Descriptions", "descriptions", catalog.descriptions || [], "descriptions", null],
  ];

  return (
    <div className="row g-3">
      <div className="col-lg-6">
        <div className="inv-panel h-100">
          <h2 className="inv-section-title">Units</h2>
          <div className="row g-2">
            <div className="col-6"><input className="form-control" placeholder="Name, for example Kilogram" value={drafts.uoms} onChange={(e) => setDrafts({ ...drafts, uoms: e.target.value })} /></div>
            <div className="col-6"><input className="form-control" placeholder="Short, for example kg" value={unit.abbreviation} onChange={(e) => setUnit({ ...unit, abbreviation: e.target.value })} /></div>
            <div className="col-6">
              <select className="form-select" value={unit.base_code} onChange={(e) => setUnit({ ...unit, base_code: e.target.value, factor: e.target.value === "piece" ? "1" : unit.factor })}>
                <option value="piece">Counted items, such as piece or box</option>
                <option value="mass">Weight, such as gram or kilogram</option>
                <option value="volume">Liquid, such as ml or litre</option>
              </select>
            </div>
            <div className="col-6"><input className="form-control" placeholder="How many small units make one. Use 1 if unsure" value={unit.factor} onChange={(e) => setUnit({ ...unit, factor: e.target.value })} /></div>
          </div>
          <button type="button" className="btn btn-danger btn-sm mt-2" onClick={addUnit}>Add unit</button>
          <ul className="mt-3 mb-0">{(catalog.uoms || []).map((row) => <li key={row.id}>{row.name} ({row.abbreviation})</li>)}</ul>
        </div>
      </div>
      {blocks.map(([title, key, rows, path]) => (
        <div className="col-lg-6" key={key}>
          <div className="inv-panel h-100">
            <h2 className="inv-section-title">{title}</h2>
            <div className="d-flex gap-2">
              <input className="form-control" placeholder={`Add ${title.toLowerCase()}`} value={drafts[key]} onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })} />
              <button type="button" className="btn btn-danger" onClick={() => addSimple(key, path)}>Add</button>
            </div>
            <ul className="mt-3 mb-0">{rows.map((row) => <li key={row.id}>{row.name}</li>)}</ul>
          </div>
        </div>
      ))}
      <div className="col-12">
        <div className="inv-panel">
          <h2 className="inv-section-title">Who can see a category</h2>
          <GrantEditor catalog={catalog} />
        </div>
      </div>
    </div>
  );
}

function GrantEditor({ catalog }) {
  const [grants, setGrants] = useState([]);
  const [grant, setGrant] = useState({
    category_id: "",
    role_code: "7",
    can_view: true,
    can_receive: true,
    can_issue: true,
    can_adjust: false,
    can_recipe: true,
  });
  useEffect(() => {
    UserServices.inventoryGrants().then((res) => setGrants(res.data?.data || [])).catch(() => {});
  }, [catalog]);
  const save = async () => {
    if (!grant.category_id) return explain("Choose the category this login can see.");
    try {
      await UserServices.inventorySave("grants", grant);
      const res = await UserServices.inventoryGrants();
      setGrants(res.data?.data || []);
      setGrant({ ...grant, category_id: "" });
      Swal.fire({ title: "Saved", text: "That login can now use this category.", icon: "success", timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Catalog", messageOf(err, "Could not save access."), "error");
    }
  };
  return (
    <>
      <div className="row g-2 align-items-end">
        <div className="col-md-3">
          <select className="form-select" value={grant.category_id} onChange={(e) => setGrant({ ...grant, category_id: e.target.value })}>
            <option value="">Category</option>
            {(catalog.categories || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </div>
        <div className="col-md-3">
          <select className="form-select" value={grant.role_code} onChange={(e) => setGrant({ ...grant, role_code: e.target.value })}>
            {ROLE_OPTIONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        </div>
        {["can_view", "can_receive", "can_issue", "can_adjust", "can_recipe"].map((flag) => (
          <div className="col-auto" key={flag}>
            <label>
              <input className="form-check-input me-1" type="checkbox" checked={!!grant[flag]} onChange={(e) => setGrant({ ...grant, [flag]: e.target.checked })} />
              {flag.replace("can_", "")}
            </label>
          </div>
        ))}
        <div className="col-auto"><button type="button" className="btn btn-danger btn-sm" onClick={save}>Save access</button></div>
      </div>
    </>
  );
}

function SetupPage({ meta, section = "all" }) {
  const [catalog, setCatalog] = useState(meta);
  const [items, setItems] = useState([]);
  const [grants, setGrants] = useState([]);
  const [category, setCategory] = useState({ name: "", code: "" });
  const [uom, setUom] = useState({ name: "", abbreviation: "", base_code: "mass", factor: "1" });
  const [brand, setBrand] = useState("");
  const [item, setItem] = useState(emptyProduct());
  const [miss, setMiss] = useState("");
  const [grant, setGrant] = useState({
    category_id: "",
    role_code: "7",
    can_view: true,
    can_receive: true,
    can_issue: true,
    can_adjust: false,
    can_recipe: true,
  });

  const load = () => {
    UserServices.inventoryMeta().then((res) => setCatalog(res.data?.data || meta)).catch(() => {});
    UserServices.inventoryItems().then((res) => setItems(res.data?.data || [])).catch(() => {});
    UserServices.inventoryGrants().then((res) => setGrants(res.data?.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (path, payload) => {
    try {
      await UserServices.inventorySave(path, payload);
      load();
    } catch (err) {
      Swal.fire("Setup", messageOf(err, "Could not save."), "error");
    }
  };

  const addProduct = async () => {
    const name = item.name.trim();
    if (!name) {
      setMiss("name");
      return explain("Type the product name first.");
    }
    if (!item.category_id) {
      setMiss("category");
      return explain("Choose a category. Add one on the Catalog page if the list is empty.");
    }
    if (!item.uom_id) {
      setMiss("unit");
      return explain("Choose the unit, such as piece, box, or kilogram.");
    }
    const reorder = item.reorder_level === "" ? 0 : Number(item.reorder_level);
    if (!Number.isFinite(reorder) || reorder < 0) {
      setMiss("reorder");
      return explain("Reorder level should be empty, 0, or a number above 0.");
    }
    setMiss("");
    try {
      await UserServices.inventorySave("items", { ...item, name, reorder_level: reorder });
      setItem(emptyProduct());
      load();
      Swal.fire({
        title: "Saved",
        text: `${name} is added. The form is clear for the next product.`,
        icon: "success",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire("Product", messageOf(err, "Could not save this product."), "error");
    }
  };

  if (!meta.access?.can_setup) {
    return <p className="mb-0">Only Super Admin can edit the catalog.</p>;
  }

  const show = (name) => section === "all" || section === name;

  return (
    <div className="row g-4">
      {show("categories") ? <div className="col-lg-6">
        <h6>Category</h6>
        <input className="form-control mb-2" placeholder="Name" value={category.name} onChange={(e) => setCategory({ ...category, name: e.target.value })} />
        <input className="form-control mb-2" placeholder="Code" value={category.code} onChange={(e) => setCategory({ ...category, code: e.target.value })} />
        <button className="btn btn-dark btn-sm" onClick={() => save("categories", category)}>Add category</button>
        <ul className="mt-2 mb-0">{(catalog.categories || []).map((row) => <li key={row.id}>{row.name}</li>)}</ul>
      </div> : null}
      {show("units") ? <div className="col-lg-6">
        <h6>Unit</h6>
        <input className="form-control mb-2" placeholder="Name" value={uom.name} onChange={(e) => setUom({ ...uom, name: e.target.value })} />
        <input className="form-control mb-2" placeholder="Abbreviation" value={uom.abbreviation} onChange={(e) => setUom({ ...uom, abbreviation: e.target.value })} />
        <input className="form-control mb-2" placeholder="Base (mass, volume, piece)" value={uom.base_code} onChange={(e) => setUom({ ...uom, base_code: e.target.value })} />
        <input className="form-control mb-2" placeholder="Factor" value={uom.factor} onChange={(e) => setUom({ ...uom, factor: e.target.value })} />
        <button className="btn btn-dark btn-sm" onClick={() => save("uoms", uom)}>Add unit</button>
        <ul className="mt-2 mb-0">{(catalog.uoms || []).map((row) => <li key={row.id}>{row.name} ({row.abbreviation}) Â· {row.base_code} Ã— {row.factor}</li>)}</ul>
      </div> : null}
      {show("brands") ? <div className="col-lg-6">
        <h6>Brand</h6>
        <input className="form-control mb-2" placeholder="Brand name" value={brand} onChange={(e) => setBrand(e.target.value)} />
        <button className="btn btn-dark btn-sm" onClick={() => save("brands", { name: brand })}>Add brand</button>
        <ul className="mt-2 mb-0">{(catalog.brands || []).map((row) => <li key={row.id}>{row.name}</li>)}</ul>
      </div> : null}
      {show("products") ? <div className="col-lg-6">
        <h6>Product</h6>
        <p className="text-muted small">Name, category, and unit are required. The form clears after the product is saved.</p>
        <label className="form-label">Product name</label>
        <input className={`form-control mb-2 ${miss === "name" ? "is-invalid" : ""}`} placeholder="For example Tissue Box" value={item.name} onChange={(e) => { setMiss(""); setItem({ ...item, name: e.target.value }); }} />
        <label className="form-label">Category</label>
        <select className={`form-select mb-2 ${miss === "category" ? "is-invalid" : ""}`} value={item.category_id} onChange={(e) => { setMiss(""); setItem({ ...item, category_id: e.target.value }); }}>
          <option value="">Choose a category</option>
          {(catalog.categories || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        </select>
        <label className="form-label">Unit</label>
        <select className={`form-select mb-2 ${miss === "unit" ? "is-invalid" : ""}`} value={item.uom_id} onChange={(e) => { setMiss(""); setItem({ ...item, uom_id: e.target.value }); }}>
          <option value="">Choose a unit</option>
          {(catalog.uoms || []).map((row) => <option key={row.id} value={row.id}>{row.name} ({row.abbreviation})</option>)}
        </select>
        <label className="form-label">Brand, if it has one</label>
        <select className="form-select mb-2" value={item.brand_id} onChange={(e) => setItem({ ...item, brand_id: e.target.value })}>
          <option value="">No brand</option>
          {(catalog.brands || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        </select>
        <label className="form-label">Color, if it has one</label>
        <select className="form-select mb-2" value={item.color_id || ""} onChange={(e) => setItem({ ...item, color_id: e.target.value })}>
          <option value="">No color</option>
          {(catalog.colors || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        </select>
        <label className="form-label">Description, if you saved one</label>
        <select className="form-select mb-2" value={item.description || ""} onChange={(e) => setItem({ ...item, description: e.target.value })}>
          <option value="">No description</option>
          {(catalog.descriptions || []).map((row) => <option key={row.id} value={row.name}>{row.name}</option>)}
        </select>
        <label className="form-label">Warn me when stock is at or below</label>
        <input className={`form-control mb-2 ${miss === "reorder" ? "is-invalid" : ""}`} placeholder="0" value={item.reorder_level} onChange={(e) => { setMiss(""); setItem({ ...item, reorder_level: e.target.value }); }} />
        <button className="btn btn-danger btn-sm" onClick={addProduct}>Add product</button>
      </div> : null}
      {show("products") ? <div className="col-12">
        <h6>Items</h6>
        <table className="table table-sm">
          <thead><tr><th>Name</th><th>Category</th><th>Unit</th><th>Color</th><th>Description</th><th>Active</th></tr></thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.category?.name}</td>
                <td>{row.uom?.abbreviation}</td>
                <td>{row.color?.name || ""}</td>
                <td>{row.description || ""}</td>
                <td>
                  <button className="btn btn-sm btn-outline-dark" onClick={() => save(`items/${row.id}`, { is_active: !row.is_active, name: row.name })}>
                    {row.is_active ? "Active" : "Inactive"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div> : null}
      {show("categories") ? <div className="col-12">
        <h6>Who can see a category</h6>
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <select className="form-select" value={grant.category_id} onChange={(e) => setGrant({ ...grant, category_id: e.target.value })}>
              <option value="">Category</option>
              {(catalog.categories || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <select className="form-select" value={grant.role_code} onChange={(e) => setGrant({ ...grant, role_code: e.target.value })}>
              {ROLE_OPTIONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          </div>
          {["can_view", "can_receive", "can_issue", "can_adjust", "can_recipe"].map((flag) => (
            <div className="col-auto" key={flag}>
              <label className="form-check-label">
                <input className="form-check-input me-1" type="checkbox" checked={!!grant[flag]} onChange={(e) => setGrant({ ...grant, [flag]: e.target.checked })} />
                {flag.replace("can_", "")}
              </label>
            </div>
          ))}
          <div className="col-auto">
            <button className="btn btn-dark btn-sm" onClick={() => save("grants", grant)}>Save access</button>
          </div>
        </div>
        <ul className="mt-3 mb-0">
          {grants.map((row) => (
            <li key={row.id}>
              {(catalog.categories || []).find((category) => category.id === row.category_id)?.name || "Category"} , role {ROLE_OPTIONS.find((option) => option[0] === String(row.role_code))?.[1] || row.role_code}
              {row.can_view ? " view" : ""}
              {row.can_receive ? " receive" : ""}
              {row.can_issue ? " issue" : ""}
              {row.can_adjust ? " adjust" : ""}
              {row.can_recipe ? " recipe" : ""}
            </li>
          ))}
        </ul>
      </div> : null}
    </div>
  );
}

function MovementsPage({ meta, apiBranch }) {
  const [params, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    category_id: "",
    item_id: params.get("item_id") || "",
    type: "",
    start_date: "",
    end_date: "",
    branch_id: params.get("branch_id") || "",
  });
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);

  useEffect(() => {
    UserServices.inventoryItems({ active_only: 1 }).then((res) => setItems(res.data?.data || [])).catch(() => {});
  }, []);

  const load = () => {
    const query = {
      ...filters,
      branch_id: filters.branch_id || apiBranch,
    };
    UserServices.inventoryMovements(query)
      .then((res) => setRows(res.data?.data || []))
      .catch((err) => Swal.fire("Movements", messageOf(err, "Could not load history."), "error"));
  };

  useEffect(() => { load(); }, [apiBranch, filters.item_id]);

  return (
    <>
      <div className="row g-2 mb-3">
        <div className="col-md-2">
          <select className="form-select" value={filters.category_id} onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}>
            <option value="">Category</option>
            {(meta.categories || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select" value={filters.item_id} onChange={(e) => { setFilters({ ...filters, item_id: e.target.value }); setSearchParams({}); }}>
            <option value="">Item</option>
            {items.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">Type</option>
            {Object.entries(TYPE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>
        <div className="col-md-2"><input type="date" className="form-control" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} /></div>
        <div className="col-md-2"><input type="date" className="form-control" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} /></div>
        <div className="col-md-2"><button className="btn btn-dark w-100" onClick={load}>Filter</button></div>
      </div>
      <MovementTable rows={rows} />
    </>
  );
}

function MovementTable({ rows }) {
  return (
    <div className="table-responsive">
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Date</th><th>Item</th><th>Branch</th><th>Type</th><th>Qty</th><th>After</th><th>Given to</th><th>By</th><th>Note</th><th>Link</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={row.type === "shortage" ? "inventory-low" : ""}>
              <td>{String(row.created_at || "").replace("T", " ").slice(0, 16)}</td>
              <td>{row.item_name}</td>
              <td>{branchName(row.branch_id)}</td>
              <td>{TYPE_LABELS[row.type] || row.type}</td>
              <td>{row.qty} {row.uom}</td>
              <td>{row.qty_after}</td>
              <td>{row.issued_to_name || ""}</td>
              <td>{row.user_name}</td>
              <td>{row.note}</td>
              <td>{row.order_id ? `Order ${row.order_id}` : row.transfer_id ? `Transfer ${row.transfer_id}` : ""}</td>
            </tr>
          ))}
          {!rows.length ? <tr><td colSpan="10">No movements for these filters.</td></tr> : null}
        </tbody>
      </table>
    </div>
  );
}

function RecipesPage({ meta, apiBranch }) {
  const [products, setProducts] = useState([]);
  const [packageId, setPackageId] = useState("");
  const [lines, setLines] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ item_id: "", qty: "", uom_id: "" });

  useEffect(() => {
    UserServices.inventoryCafeProducts({ branch_id: apiBranch })
      .then((res) => setProducts(res.data?.data || []))
      .catch((err) => Swal.fire("Recipes", messageOf(err, "Could not load cafe products."), "error"));
    UserServices.inventoryItems({ active_only: 1 }).then((res) => setItems(res.data?.data || [])).catch(() => {});
  }, [apiBranch]);

  const loadLines = (id) => {
    if (!id) { setLines([]); return; }
    UserServices.inventoryRecipes({ package_id: id })
      .then((res) => setLines(res.data?.data || []))
      .catch((err) => Swal.fire("Recipes", messageOf(err, "Could not load the recipe."), "error"));
  };

  const selectedItem = useMemo(() => items.find((row) => String(row.id) === String(form.item_id)), [items, form.item_id]);
  const unitChoices = (meta.uoms || []).filter((unit) => {
    if (!selectedItem?.uom) return true;
    return unit.id === selectedItem.uom_id || unit.base_code === selectedItem.uom.base_code;
  });

  const save = async () => {
    if (!form.item_id) return explain("Choose the stock item this product uses.");
    if (!(Number(form.qty) > 0)) return explain("Type how much one product uses. It has to be more than zero.");
    if (!form.uom_id) return explain("Choose the unit for that amount.");
    try {
      await UserServices.inventorySave("recipes", { package_id: packageId, ...form });
      setForm({ item_id: "", qty: "", uom_id: "" });
      loadLines(packageId);
      Swal.fire({ title: "Saved", text: "This rule is saved. The fields are clear for the next item.", icon: "success", timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Recipes", messageOf(err, "Could not save the recipe."), "error");
    }
  };

  const remove = async (id) => {
    try {
      await UserServices.inventoryDeleteRecipe(id);
      loadLines(packageId);
    } catch (err) {
      Swal.fire("Recipes", messageOf(err, "Could not remove that line."), "error");
    }
  };

  return (
    <>
      <h6 className="mb-1">Cafe product deduction rules</h6>
      <p className="text-muted small">How much of each stock item one cafe product uses. Saving a rule does not change stock. A punched order does.</p>
      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <select className="form-select" value={packageId} onChange={(e) => { setPackageId(e.target.value); loadLines(e.target.value); }}>
            <option value="">Cafe product</option>
            {products.map((row) => <option key={row.id} value={row.id}>{row.name} ({branchName(row.branch_id)})</option>)}
          </select>
        </div>
      </div>
      {packageId ? (
        <>
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <select className="form-select" value={form.item_id} onChange={(e) => {
                const next = items.find((row) => String(row.id) === e.target.value);
                setForm({ ...form, item_id: e.target.value, uom_id: next?.uom_id || "" });
              }}>
                <option value="">Item</option>
                {items.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <input className="form-control" placeholder="Qty for one" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
            </div>
            <div className="col-md-3">
              <select className="form-select" value={form.uom_id} onChange={(e) => setForm({ ...form, uom_id: e.target.value })}>
                <option value="">Unit</option>
                {unitChoices.map((row) => <option key={row.id} value={row.id}>{row.abbreviation}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <button className="btn btn-dark w-100" onClick={save}>Add line</button>
            </div>
          </div>
          <table className="table table-sm">
            <thead><tr><th>Item</th><th>For one product</th><th></th></tr></thead>
            <tbody>
              {lines.map((row) => (
                <tr key={row.id}>
                  <td>{row.item?.name}</td>
                  <td>{row.qty} {row.uom?.abbreviation}</td>
                  <td><button className="btn btn-sm btn-outline-danger" onClick={() => remove(row.id)}>Remove</button></td>
                </tr>
              ))}
              {!lines.length ? <tr><td colSpan="3">This product has no recipe yet. Selling it will not change stock.</td></tr> : null}
            </tbody>
          </table>
        </>
      ) : null}
    </>
  );
}

function ReportsPage({ meta, apiBranch }) {
  const [params] = useSearchParams();
  const [kind, setKind] = useState(params.get("kind") || "stock");
  const [categoryId, setCategoryId] = useState("");
  const [rows, setRows] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = () => {
    UserServices.inventoryReport(kind, {
      branch_id: apiBranch,
      category_id: categoryId || undefined,
      start_date: start || undefined,
      end_date: end || undefined,
    }).then((res) => setRows(res.data?.data || []))
      .catch((err) => Swal.fire("Reports", messageOf(err, "Could not load the report."), "error"));
  };

  useEffect(() => { load(); }, [kind, apiBranch, categoryId]);

  const exportRows = () => {
    if (kind === "usage") {
      downloadCsv("inventory-usage.csv", ["Product", "Item", "Qty", "Unit"], rows.map((row) => [row.product_name, row.item_name, row.qty, row.uom]));
      return;
    }
    if (kind === "stock" || kind === "low") {
      downloadCsv("inventory-stock.csv", ["Item", "Category", "Branch", "On hand", "Unit", "Reorder"], rows.map((row) => [row.name, row.category_name, row.branch_name, row.qty_on_hand, row.uom, row.reorder_level]));
      return;
    }
    downloadCsv(`inventory-${kind}.csv`, ["Date", "Item", "Branch", "Type", "Qty", "After", "Given to", "Note"], rows.map((row) => [row.created_at, row.item_name, branchName(row.branch_id), row.type, row.qty, row.qty_after, row.issued_to_name, row.note]));
  };

  return (
    <>
      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <select className="form-select" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="stock">Current stock</option>
            <option value="low">Low stock</option>
            <option value="shortages">Shortages</option>
            <option value="usage">Usage by cafe product</option>
            <option value="transfers">Transfers</option>
          </select>
        </div>
        <div className="col-md-3">
          <select className="form-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">All categories</option>
            {(meta.categories || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </div>
        <div className="col-md-2"><input type="date" className="form-control" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div className="col-md-2"><input type="date" className="form-control" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
        <div className="col-md-2 d-flex gap-2">
          <button className="btn btn-dark" onClick={load}>Show</button>
          <button className="btn btn-outline-dark" onClick={exportRows}>Export</button>
        </div>
      </div>
      {kind === "usage" ? (
        <table className="table table-sm">
          <thead><tr><th>Product</th><th>Item</th><th>Used</th></tr></thead>
          <tbody>
            {rows.map((row, index) => <tr key={index}><td>{row.product_name}</td><td>{row.item_name}</td><td>{row.qty} {row.uom}</td></tr>)}
            {!rows.length ? <tr><td colSpan="3">No cafe usage in these filters.</td></tr> : null}
          </tbody>
        </table>
      ) : kind === "stock" || kind === "low" ? (
        <table className="table table-sm">
          <thead><tr><th>Item</th><th>Category</th><th>Branch</th><th>On hand</th><th>Reorder</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.item_id}-${row.branch_id}`} className={row.low ? "inventory-low" : ""}>
                <td>{row.name}</td><td>{row.category_name}</td><td>{row.branch_name}</td><td>{row.qty_on_hand} {row.uom}</td><td>{row.reorder_level}</td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan="5">Nothing to show.</td></tr> : null}
          </tbody>
        </table>
      ) : (
        <MovementTable rows={rows} />
      )}
    </>
  );
}

const DEMAND_STATUS = {
  pending: "Waiting",
  returned: "Needs changes",
  approved: "Approved",
  rejected: "Rejected",
};

const DEMAND_ACTION = {
  submitted: "Asked",
  updated: "Sent again",
  approved: "Approved",
  rejected: "Rejected",
  returned: "Sent back",
};

function DemandsPage({ apiBranch, canDecide }) {
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [form, setForm] = useState({ item_id: "", qty: "", note: "" });
  const [edits, setEdits] = useState({});
  const [decision, setDecision] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    UserServices.inventoryDemands({ branch_id: apiBranch, status: status || undefined })
      .then((res) => setRows(res.data?.data || []))
      .catch((err) => Swal.fire("Demands", messageOf(err, "Could not load demands."), "error"));
  };

  useEffect(() => {
    UserServices.inventoryItems({ active_only: 1 }).then((res) => setItems(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [apiBranch, status]);

  const ask = async () => {
    if (!form.item_id) return explain("Choose the product you need.");
    if (!(Number(form.qty) > 0)) return explain("Type how many you need. It has to be more than zero.");
    if (!form.note.trim()) return explain("Write why you need it, so the manager knows.");
    setBusy(true);
    try {
      await UserServices.inventoryDemandSave({ ...form, branch_id: apiBranch });
      setForm({ item_id: "", qty: "", note: "" });
      load();
      Swal.fire({ title: "Sent", text: "Your demand is waiting for a manager.", icon: "success", timer: 1600, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Demand", messageOf(err, "Could not send this demand."), "error");
    } finally {
      setBusy(false);
    }
  };

  const sendAgain = async (row) => {
    const edit = edits[row.id] || { item_id: String(row.item_id), qty: String(row.qty), note: row.note || "" };
    if (!edit.item_id) return explain("Choose the product.");
    if (!(Number(edit.qty) > 0)) return explain("Type how many you need. It has to be more than zero.");
    if (!String(edit.note || "").trim()) return explain("Write why you need it.");
    setBusy(true);
    try {
      await UserServices.inventoryDemandUpdate(row.id, { ...edit, branch_id: apiBranch });
      setEdits((current) => ({ ...current, [row.id]: undefined }));
      load();
      Swal.fire({ title: "Sent again", text: "The manager can review it now.", icon: "success", timer: 1600, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Demand", messageOf(err, "Could not update this demand."), "error");
    } finally {
      setBusy(false);
    }
  };

  const decide = async (row, action, note) => {
    if ((action === "return" || action === "reject") && !String(note || "").trim()) {
      return explain(action === "return" ? "Write what should be changed." : "Write why this is rejected.");
    }
    setBusy(true);
    try {
      await UserServices.inventoryDemandDecide(row.id, { action, note: note || "", branch_id: apiBranch });
      setDecision(null);
      load();
      const text = action === "approve" ? "Demand approved. Stock is unchanged until someone issues it." : action === "return" ? "Sent back. They can change it and send it again." : "Demand rejected.";
      Swal.fire({ title: "Saved", text, icon: "success", timer: 1800, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Demand", messageOf(err, "Could not update this demand."), "error");
    } finally {
      setBusy(false);
    }
  };

  const waiting = rows.filter((row) => row.status === "pending").length;

  return (
    <>
      <h6 className="mb-1">Ask for a product</h6>
      <p className="text-muted small">Send what you need. An admin or branch manager can approve it, reject it, or send it back to change. Approving does not take stock out. Issue the stock after it is approved.</p>
      <div className="row g-2 mb-4">
        <div className="col-md-4">
          <label className="form-label">Product</label>
          <select className="form-select" value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })}>
            <option value="">Choose a product</option>
            {items.map((row) => <option key={row.id} value={row.id}>{row.name} ({row.uom?.abbreviation})</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">How many</label>
          <input className="form-control" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Why you need it</label>
          <input className="form-control" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button type="button" className="btn btn-danger w-100" disabled={busy} onClick={ask}>Send demand</button>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">{canDecide ? "Demands at this branch" : "Your demands"}{waiting ? ` · ${waiting} waiting` : ""}</h6>
        <select className="form-select" style={{ maxWidth: 220 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All history</option>
          <option value="pending">Waiting</option>
          <option value="returned">Needs changes</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {!rows.length ? <p className="mb-0">No demands for this view.</p> : null}
      {rows.map((row) => {
        const edit = edits[row.id] || { item_id: String(row.item_id), qty: String(row.qty), note: row.note || "" };
        const open = decision?.id === row.id ? decision.action : "";
        return (
          <article className="inv-demand" key={row.id}>
            <div className="d-flex flex-wrap justify-content-between gap-2">
              <div>
                <strong>{row.item_name}</strong> · {row.qty} {row.uom}
                <div className="small text-muted">{row.requested_by_name || "Staff"} · {row.created_at}</div>
              </div>
              <span className={`inv-status ${row.status}`}>{DEMAND_STATUS[row.status] || row.status}</span>
            </div>
            <p className="mb-1 mt-2">{row.note}</p>
            <div className="small text-muted">History</div>
            <ul className="inv-history small">
              {(row.events || []).map((event) => (
                <li key={event.id}>
                  {event.created_at} · {DEMAND_ACTION[event.action] || event.action}
                  {event.user_name ? ` by ${event.user_name}` : ""}
                  {event.qty ? ` · ${event.qty} ${row.uom}` : ""}
                  {event.note ? ` · ${event.note}` : ""}
                </li>
              ))}
            </ul>

            {row.mine && row.status === "returned" ? (
              <div className="row g-2 mt-2">
                <div className="col-md-4">
                  <select className="form-select" value={edit.item_id} onChange={(e) => setEdits({ ...edits, [row.id]: { ...edit, item_id: e.target.value } })}>
                    {items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <input className="form-control" value={edit.qty} onChange={(e) => setEdits({ ...edits, [row.id]: { ...edit, qty: e.target.value } })} />
                </div>
                <div className="col-md-4">
                  <input className="form-control" value={edit.note} onChange={(e) => setEdits({ ...edits, [row.id]: { ...edit, note: e.target.value } })} />
                </div>
                <div className="col-md-2">
                  <button type="button" className="btn btn-dark w-100" disabled={busy} onClick={() => sendAgain(row)}>Send again</button>
                </div>
              </div>
            ) : null}

            {canDecide && row.status === "pending" ? (
              <div className="mt-2">
                <button type="button" className="btn btn-dark btn-sm me-2" disabled={busy} onClick={() => decide(row, "approve", "")}>Approve</button>
                <button type="button" className="btn btn-outline-dark btn-sm me-2" disabled={busy} onClick={() => setDecision({ id: row.id, action: "return", note: "" })}>Send back</button>
                <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => setDecision({ id: row.id, action: "reject", note: "" })}>Reject</button>
                {open ? (
                  <div className="row g-2 mt-2">
                    <div className="col-md-8">
                      <input className="form-control" placeholder={open === "return" ? "What should they change?" : "Why is this rejected?"} value={decision.note} onChange={(e) => setDecision({ ...decision, note: e.target.value })} />
                    </div>
                    <div className="col-md-4">
                      <button type="button" className="btn btn-danger" disabled={busy} onClick={() => decide(row, open, decision.note)}>{open === "return" ? "Send back" : "Reject"}</button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </article>
        );
      })}
    </>
  );
}

function karachiToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Karachi" }).format(new Date());
}

function WaterBottlesPage({ apiBranch }) {
  const [entryDate, setEntryDate] = useState(karachiToday());
  const [type, setType] = useState("received");
  const [qty, setQty] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [filterType, setFilterType] = useState("");
  const [report, setReport] = useState({ rows: [], on_hand: 0 });
  const [busy, setBusy] = useState(false);

  const load = () => {
    UserServices.inventoryWaterBottles({
      branch_id: apiBranch,
      start_date: start || undefined,
      end_date: end || undefined,
      type: filterType || undefined,
    })
      .then((res) => setReport(res.data?.data || { rows: [], on_hand: 0 }))
      .catch((err) => Swal.fire("Water bottles", messageOf(err, "Could not load the bottles."), "error"));
  };

  useEffect(() => { load(); }, [apiBranch, start, end, filterType]);

  const save = async () => {
    if (!entryDate) return explain("Choose the date.");
    if (type !== "received" && type !== "delivered") return explain("Choose received or delivered.");
    const count = Number(qty);
    if (!Number.isInteger(count) || count < 1) return explain("Type a whole number of bottles, more than zero.");
    setBusy(true);
    try {
      await UserServices.inventoryWaterSave({
        branch_id: apiBranch,
        entry_date: entryDate,
        type,
        qty: count,
      });
      setQty("");
      load();
      Swal.fire({
        title: "Saved",
        text: `${count} bottles ${type === "received" ? "received" : "delivered"}. The number box is clear.`,
        icon: "success",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire("Water bottles", messageOf(err, "Could not save."), "error");
    } finally {
      setBusy(false);
    }
  };

  const rows = report.rows || [];
  const showReceived = filterType !== "delivered";
  const showDelivered = filterType !== "received";
  const receivedTotal = rows.reduce((sum, row) => sum + Number(row.received || 0), 0);
  const deliveredTotal = rows.reduce((sum, row) => sum + Number(row.delivered || 0), 0);

  return (
    <>
      <h6 className="mb-1">Soloni water bottles</h6>
      <p className="text-muted small mb-3">20 litre dispenser bottles. Choose the date, choose received or delivered, then type how many bottles.</p>
      <div className="row g-2 align-items-end mb-4">
        <div className="col-md-3">
          <label className="form-label">Date</label>
          <input type="date" className="form-control" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
        </div>
        <div className="col-md-3">
          <label className="form-label">What happened</label>
          <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="received">Received</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">Number of bottles</label>
          <input className="form-control" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />
        </div>
        <div className="col-md-3">
          <button type="button" className="btn btn-danger w-100" disabled={busy} onClick={save}>Add</button>
        </div>
      </div>

      <p className="mb-3"><strong>On hand now: {report.on_hand ?? 0} bottles</strong></p>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-3">
          <label className="form-label">From</label>
          <input type="date" className="form-control" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="col-md-3">
          <label className="form-label">To</label>
          <input type="date" className="form-control" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <div className="col-md-3">
          <label className="form-label">Show</label>
          <select className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">Received and delivered</option>
            <option value="received">Received</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              <th>Date</th>
              {showReceived ? <th className="text-end">Received</th> : null}
              {showDelivered ? <th className="text-end">Delivered</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.date}>
                <td>{row.date}</td>
                {showReceived ? <td className="text-end">{row.received}</td> : null}
                {showDelivered ? <td className="text-end">{row.delivered}</td> : null}
              </tr>
            ))}
            {rows.length ? (
              <tr>
                <td><strong>Total</strong></td>
                {showReceived ? <td className="text-end"><strong>{receivedTotal}</strong></td> : null}
                {showDelivered ? <td className="text-end"><strong>{deliveredTotal}</strong></td> : null}
              </tr>
            ) : (
              <tr><td colSpan={showReceived && showDelivered ? 3 : 2}>No bottles for these dates.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
